import { useState, useEffect } from "react";
import Select from "react-select";

const PREDEFINED_CATEGORIES = [
    "General",
    "Billing",
    "Shipping",
    "Technical Support",
    "Account Management"
];

const categoryOptions = [
    ...PREDEFINED_CATEGORIES.map(cat => ({ value: cat, label: cat })),
    { value: "Other", label: "Other (Type your own)" }
];

export default function FAQModal({ show, onClose, onSubmit, initialData, isLoading }) {
    const [formData, setFormData] = useState({
        category: "",
        question: "",
        answer: "",
        sort: 1,
        isActive: true,
    });

    const [isCustomCategory, setIsCustomCategory] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (show) {
            if (initialData) {
                const existingCategory = initialData.category || "";

                if (existingCategory && !PREDEFINED_CATEGORIES.includes(existingCategory)) {
                    setIsCustomCategory(true);
                    setSelectedOption({ value: "Other", label: "Other (Type your own)" });
                } else if (existingCategory) {
                    setIsCustomCategory(false);
                    setSelectedOption({ value: existingCategory, label: existingCategory });
                } else {
                    setIsCustomCategory(false);
                    setSelectedOption(null);
                }

                setFormData({
                    category: existingCategory,
                    question: initialData.question || "",
                    answer: initialData.answer || "",
                    sort: initialData.sort || 1,
                    isActive: initialData.isActive ?? true,
                });
            } else {
                setIsCustomCategory(false);
                setSelectedOption(null);
                setFormData({
                    category: "",
                    question: "",
                    answer: "",
                    sort: 1,
                    isActive: true,
                });
            }
            setErrors({});
        }
    }, [show, initialData]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.category.trim()) {
            newErrors.category = "Category is required.";
        }
        if (!formData.question.trim()) {
            newErrors.question = "Question is required.";
        }
        if (!formData.answer.trim()) {
            newErrors.answer = "Answer is required.";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            onSubmit(formData);
        }
    };

    const handleCategoryChange = (selected) => {
        setSelectedOption(selected);

        if (!selected) {
            setFormData({ ...formData, category: "" });
            setIsCustomCategory(false);
            return;
        }

        if (selected.value === "Other") {
            setIsCustomCategory(true);
            setFormData({ ...formData, category: "" }); 
        } else {
            setIsCustomCategory(false);
            setFormData({ ...formData, category: selected.value });
            if (errors.category) setErrors({ ...errors, category: null });
        }
    };

    if (!show) return null;

    return (
        <>
            <div className="modal-backdrop fade show" style={{ zIndex: 9998 }}></div>

            <div className="modal fade show" style={{ display: "block", zIndex: 9999 }} tabIndex="-1">

                <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable pt-5">
                    <div className="modal-content shadow-lg">
                        <form onSubmit={handleSubmit} noValidate>
                            <div className="modal-header bg-primary text-white">
                                <h5 className="modal-title">
                                    <i className="fas fa-question-circle mr-2"></i>
                                    {initialData ? "Edit FAQ" : "Add New FAQ"}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={onClose}
                                ></button>
                            </div>

                            <div className="modal-body p-3">

                                {/* React-Select Dropdown & Conditional Input */}
                                <div className="mb-3">
                                    <label className="form-label fw-bold">
                                        Category <span className="text-danger">*</span>
                                    </label>

                                    <Select
                                        options={categoryOptions}
                                        value={selectedOption}
                                        onChange={handleCategoryChange}
                                        placeholder="Select a category..."
                                        isClearable
                                        className={errors.category && !isCustomCategory ? "is-invalid" : ""}
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                borderColor: errors.category && !isCustomCategory ? "#dc3545" : base.borderColor,
                                                "&:hover": {
                                                    borderColor: errors.category && !isCustomCategory ? "#dc3545" : base.borderColor
                                                }
                                            })
                                        }}
                                    />

                                    {/* Appears only if "Other" is selected */}
                                    {isCustomCategory && (
                                        <div className="mt-2">
                                            <input
                                                type="text"
                                                className={`form-control ${errors.category ? "is-invalid" : ""}`}
                                                value={formData.category}
                                                onChange={(e) => {
                                                    setFormData({ ...formData, category: e.target.value });
                                                    if (errors.category) setErrors({ ...errors, category: null });
                                                }}
                                                placeholder="Enter custom category name"
                                                autoFocus
                                            />
                                        </div>
                                    )}

                                    {errors.category && (
                                        <div className="text-danger small mt-1">{errors.category}</div>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-bold">
                                        Question <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.question ? "is-invalid" : ""}`}
                                        value={formData.question}
                                        onChange={(e) => {
                                            setFormData({ ...formData, question: e.target.value });
                                            if (errors.question) setErrors({ ...errors, question: null });
                                        }}
                                        placeholder="Enter FAQ question"
                                    />
                                    {errors.question && (
                                        <div className="invalid-feedback">{errors.question}</div>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-bold">
                                        Answer <span className="text-danger">*</span>
                                    </label>
                                    <textarea
                                        rows="4"
                                        className={`form-control ${errors.answer ? "is-invalid" : ""}`}
                                        value={formData.answer}
                                        onChange={(e) => {
                                            setFormData({ ...formData, answer: e.target.value });
                                            if (errors.answer) setErrors({ ...errors, answer: null });
                                        }}
                                        placeholder="Enter FAQ answer"
                                    />
                                    {errors.answer && (
                                        <div className="invalid-feedback">{errors.answer}</div>
                                    )}
                                </div>

                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">Sort Order</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={formData.sort}
                                            onChange={(e) =>
                                                setFormData({ ...formData, sort: Number(e.target.value) })
                                            }
                                        />
                                    </div>

                                    <div className="col-md-6 mb-3 d-flex align-items-end">
                                        <div className="form-check form-switch fs-5">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={formData.isActive}
                                                onChange={(e) =>
                                                    setFormData({ ...formData, isActive: e.target.checked })
                                                }
                                            />
                                            <label className="form-check-label ms-2 mt-1">Is Active</label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer bg-light">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={onClose}
                                >
                                    Close
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm mr-2"></span>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-save mr-2"></i>
                                            {initialData ? "Update FAQ" : "Create FAQ"}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
}